"use client";

import { SelectDropdown } from "@/components/ui/select-dropdown";
import { cn } from "@/lib/utils";
import { useAuth } from "@/providers/auth-provider";
import { useModal } from "@/providers/modal-provider";
import { ArrowRightIcon, SearchIcon } from "lucide-react";
import Link from "next/link";
import { useRef } from "react";
import { TVCListingFilters } from "./vc-listing";
import { ModalType } from "@/types/modal-type";

const backgroundImage = "/images/vc-page/vc-list-background.png";

type TMultiSelectDropdownItem = {
	id: string;
	name: string;
};

const VCListHeroSection = ({
	filters,
	setFilters,
}: {
	filters: TVCListingFilters;
	setFilters: React.Dispatch<React.SetStateAction<TVCListingFilters>>;
}) => {
	const investmentStageDropDownItems: TMultiSelectDropdownItem[] = [
		{ id: "pre-seed", name: "Pre-Seed" },
		{ id: "seed", name: "Seed" },
		{ id: "series-a", name: "Series A" },
		{ id: "series-b", name: "Series B" },
		{ id: "series-c", name: "Series C" },
		{ id: "series-d", name: "Series D" },
		{ id: "secondaries", name: "Secondaries" },
		{ id: "series-e-above", name: "Series E & Above" },
	];
	const sectorFocusTagDropDownItems: TMultiSelectDropdownItem[] = [
		{ id: "adtech", name: "Advertising (AdTech)" },
		{ id: "ai-ml", name: "AI/ML" },
		{ id: "apps", name: "Apps" },
		{ id: "big-data-analytics", name: "Big Data & Analytics" },
		{ id: "biotech", name: "BioTech" },
		{ id: "climatetech", name: "ClimateTech" },
		{ id: "cleantech", name: "CleanTech" },
		{ id: "cloudtech", name: "CloudTech" },
		{ id: "consumer", name: "Consumer" },
		{ id: "consumer-electronics", name: "Consumer Electronics" },
		{ id: "creator-economy", name: "Creator Economy" },
		{ id: "cybersecurity", name: "Cybersecurity" },
		{ id: "deeptech", name: "DeepTech" },
		{ id: "delivery", name: "Delivery" },
		{ id: "developer-tools", name: "Developer Tools" },
		{ id: "e-commerce", name: "E-Commerce" },
		{ id: "edtech", name: "EdTech" },
		{ id: "enterprise", name: "Enterprise" },
		{ id: "fashion", name: "Fashion" },
		{ id: "femtech", name: "FemTech" },
		{ id: "fintech", name: "FinTech" },
		{ id: "food-beverage", name: "Food and Beverage" },
		{ id: "hardware", name: "Hardware" },
		{ id: "health-wellness", name: "Health & Wellness" },
		{ id: "hr-tech", name: "HR Tech" },
		{ id: "insurtech", name: "InsurTech" },
		{ id: "iot", name: "IoT (Internet of Things)" },
		{ id: "legal-tech", name: "Legal Tech" },
		{ id: "martech", name: "Marketing (MarTech)" },
		{ id: "paas", name: "PaaS (Platforms)" },
		{ id: "pettech", name: "PetTech" },
		{ id: "productivity-tools", name: "Productivity Tools" },
		{ id: "proptech", name: "PropTech" },
		{ id: "retailtech", name: "RetailTech" },
		{ id: "robotics", name: "Robotics" },
		{ id: "saas", name: "SaaS" },
		{ id: "software", name: "Software" },
		{ id: "sustainability", name: "Sustainability" },
	];

	const countriesList: TMultiSelectDropdownItem[] = [
		{ id: "us", name: "United States" },
		{ id: "uk", name: "United Kingdom" },
		{ id: "sweden", name: "Sweden" },
		{ id: "spain", name: "Spain" },
		{ id: "singapore", name: "Singapore" },
		{ id: "japan", name: "Japan" },
		{ id: "israel", name: "Israel" },
		{ id: "india", name: "India" },
		{ id: "hungary", name: "Hungary" },
		{ id: "germany", name: "Germany" },
		{ id: "france", name: "France" },
		{ id: "finland", name: "Finland" },
		{ id: "czeck-republic", name: "Czeck Republic" },
		{ id: "china", name: "China" },
		{ id: "canada", name: "Canada" },
		{ id: "australia", name: "Australia" },
	];

	const searchRef = useRef<HTMLInputElement | null>(null);
	const { isLoggedIn } = useAuth();
	const { openModal } = useModal();

	const handleLocationChange = (location: string) => {
		if (isLoggedIn) {
			setFilters({
				...filters,
				hqLocation: location,
			});
		} else {
			openModal(ModalType.Auth);
		}
	};

	const handleInvestmentStageChange = (investmentStage: string) => {
		if (isLoggedIn) {
			setFilters({
				...filters,
				investmentStage,
			});
		} else {
			openModal(ModalType.Auth);
		}
	};

	const handleSectorFocusTagChange = (sectorFocusTag: string) => {
		if (isLoggedIn) {
			setFilters({
				...filters,
				sectorFocusTag,
			});
		} else {
			openModal(ModalType.Auth);
		}
	};

	const handleSearchQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (isLoggedIn) {
			setFilters({
				...filters,
				searchQuery: e.target.value,
			});
		} else {
			openModal(ModalType.Auth);
		}
	};

	return (
		<div className="relative block h-full w-full py-12">
			<div
				className="absolute inset-0 bg-cover bg-center bg-no-repeat"
				style={{ backgroundImage: `url(${backgroundImage})` }}
			/>

			<div className="relative z-10 flex h-full w-full justify-end p-4 md:p-0">
				<div className="mx-auto w-11/12 max-w-7xl md:w-full">
					<div className="flex flex-col items-center justify-center gap-2">
						<h2 className="mb-4 w-fit pb-4 text-center text-xl font-bold text-white md:text-3xl">
							Over 3000+ Proptech/Contech VCs at one place
						</h2>
						<div className="mb-4 flex items-center justify-center gap-4 max-md:flex-wrap">
							<p className="text-lg text-white/80">Accelerate Your Fundraising</p>
							<div className="flex items-center gap-2">
								{!isLoggedIn ? (
									<button
										onClick={() => openModal(ModalType.Auth)}
										className={cn(
											"flex items-center rounded-md border border-white/50 px-4 py-1 text-sm font-semibold text-white/70 shadow-lg transition"
										)}
									>
										Submit your pitch deck
										<ArrowRightIcon className="ml-2 h-4 w-4 text-white/70" />
									</button>
								) : (
									<Link
										href="https://forms.gle/4oqmvm6LQfhMPKQW9"
										target="_blank"
										className={cn(
											"flex items-center rounded-md border border-white/50 px-4 py-1 text-sm font-semibold text-white/70 shadow-lg transition"
										)}
									>
										Submit your pitch deck
										<ArrowRightIcon className="ml-2 h-4 w-4 text-white/70" />
									</Link>
								)}
							</div>
						</div>

						<h4 className="mb-1 mt-2 pb-12 text-center text-sm text-white/70">
							Join Our Elite VC Network -{" "}
							{!isLoggedIn ? (
								<button
									onClick={() => openModal(ModalType.Auth)}
									className="text-sm font-semibold text-[#F19224]"
								>
									Create VC page
								</button>
							) : (
								<Link
									href="https://forms.gle/BfPazowvztcMYCVY7"
									target="_blank"
									className="text-sm font-semibold text-[#F19224]"
								>
									Create VC page
								</Link>
							)}
						</h4>
					</div>

					<div className="absolute -bottom-[110px] left-1/2 mx-auto my-8 flex w-full max-w-7xl translate-x-[-50%] flex-col rounded-lg bg-white px-8 py-6 text-sm shadow-sm lg:flex-row lg:items-center lg:justify-between lg:gap-16">
						<div className="flex items-center gap-2 max-md:flex-wrap md:gap-6">
							<div className="flex w-full items-center gap-2 lg:w-auto">
								<span className="font-medium text-gray-600">Location</span>
								<div className="w-full lg:w-40">
									<SelectDropdown
										items={countriesList}
										selectedItem={filters.hqLocation || ""}
										setActiveItems={handleLocationChange}
										buttonLabel="All"
									/>
								</div>
							</div>

							<div className="flex w-full items-center gap-2 lg:w-auto">
								<span className="whitespace-nowrap font-medium text-gray-600">
									Investment Stage
								</span>

								<div className="w-full lg:w-40">
									<SelectDropdown
										items={investmentStageDropDownItems}
										selectedItem={filters.investmentStage || ""}
										setActiveItems={handleInvestmentStageChange}
										buttonLabel="All"
									/>
								</div>
							</div>
							<div className="flex w-full items-center gap-2 lg:w-auto">
								<span className="whitespace-nowrap font-medium text-gray-600">Sector Focus</span>
								<div className="w-full lg:w-40">
									<SelectDropdown
										items={sectorFocusTagDropDownItems}
										selectedItem={filters.sectorFocusTag || ""}
										setActiveItems={handleSectorFocusTagChange}
										buttonLabel="All"
									/>
								</div>
							</div>
						</div>

						<div className="mt-2 flex items-center justify-start gap-4 max-md:flex-wrap md:mt-0">
							<div
								className={cn(
									"group flex w-fit flex-1 items-center rounded border border-gray-200 bg-white px-4 py-2 lg:w-72",
									"transition-colors duration-150 ease-linear focus-within:border-brand-primary"
								)}
							>
								<span>
									<SearchIcon className="h-4 w-4 text-gray-400 transition-colors duration-150 ease-linear group-focus-within:text-blue-500" />
								</span>

								<input
									ref={searchRef}
									id="search"
									type="search"
									name="search"
									placeholder="Search"
									className="w-full px-2 text-sm focus:outline-none"
									onChange={handleSearchQueryChange}
								/>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default VCListHeroSection;
